export type QuantityFormatOptions = {
  isUnit?: boolean;
  uom?: string;
};

export const parseNumberLike = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;
  const cleaned = value.replace(/,/g, '').trim();
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseUomFromQuantityDisplay = (
  quantityDisplay: unknown,
): string => {
  if (typeof quantityDisplay !== 'string') return '';
  const trimmed = quantityDisplay.trim();
  if (!trimmed) return '';
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 2) return '';
  return String(tokens[tokens.length - 1] || '').trim();
};

export const formatQuantityWithUnit = (
  value: unknown,
  options: QuantityFormatOptions = {},
): string => {
  const qty = parseNumberLike(value);
  const isUnit = options.isUnit === true;
  const uom = String(options.uom || '').trim();

  if (isUnit && uom) {
    return `${qty} ${uom}`;
  }
  return String(qty);
};

export {
  getProductDisplayName,
  type ProductLike,
} from './productDisplayName';

export const buildProductFromLine = (lineItem: any): any => {
  if (!lineItem || typeof lineItem !== 'object') return null;
  return {
    _id:
      (lineItem.bp_line_item_product_id &&
      typeof lineItem.bp_line_item_product_id === 'string'
        ? lineItem.bp_line_item_product_id
        : '') ||
      lineItem.bp_product_id ||
      '',
    bp_product_name:
      lineItem.bp_line_item_product_name || lineItem.productName || '',
    bp_product_price_type:
      lineItem.bp_line_item_product_price_type ||
      lineItem.bp_product_price_type ||
      '',
    bp_product_unit_of_mass:
      lineItem.bp_line_item_unit_of_mass ||
      lineItem.bp_product_unit_of_mass ||
      '',
    bp_product_mass:
      lineItem.bp_line_item_product_mass || lineItem.bp_product_mass || '',
    bp_product_size:
      lineItem.bp_line_item_product_size || lineItem.bp_product_size || '',
    bp_product_model:
      lineItem.bp_line_item_product_model || lineItem.bp_product_model || '',
    bp_product_category:
      lineItem.bp_line_item_product_category ||
      lineItem.bp_product_category ||
      '',
    bp_product_sub_category:
      lineItem.bp_line_item_product_sub_category ||
      lineItem.bp_product_sub_category ||
      '',
    bp_product_barcode:
      lineItem.bp_line_item_product_barcode ||
      lineItem.bp_product_barcode ||
      '',
  };
};

const getVariantDisplayNameFromAny = (source: any): string => {
  if (!source || typeof source !== 'object') return '';
  const explicit = String(
    source?.bp_line_item_variant_display_name ||
      source?.bp_variant_display_name ||
      source?.variantDisplayName ||
      '',
  ).trim();
  if (explicit) return explicit;

  const attributes =
    source?.bp_line_item_variant_attributes ||
    source?.bp_variant_attributes ||
    source?.variantAttributes ||
    null;
  if (attributes && typeof attributes === 'object') {
    const joined = Object.values(attributes)
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (joined) return joined;
  }

  return '';
};

export const normalizePurchaseOrderLinesForReceipt = (order: any) => {
  if (!order || !Array.isArray(order.lines) || order.lines.length === 0) {
    return [];
  }

  return order.lines
    .filter((line: any) => Boolean(line?.lineId))
    .map((line: any) => {
      const quantityDisplay = String(line.quantityDisplay || '');
      const uomFromDisplay = parseUomFromQuantityDisplay(quantityDisplay);
      const isUnitFromDisplay = Boolean(uomFromDisplay);
      const ordered = parseNumberLike(line.ordered);
      const received = parseNumberLike(line.received);
      const remaining =
        line.remaining == null
          ? Math.max(0, ordered - received)
          : parseNumberLike(line.remaining);

      return {
        lineId: String(line.lineId),
        productName: String(line.productName || ''),
        productDisplayName:
          String(line.productDisplayName || '').trim() ||
          getVariantDisplayNameFromAny(line),
        quantityDisplay,
        product: null,
        variantId: String(
          line.bp_line_item_variant_id || line.variantId || line.bp_variant_id || '',
        ),
        variantDisplayName: getVariantDisplayNameFromAny(line),
        ordered,
        received,
        remaining,
        isUnit: line.isUnit == null ? isUnitFromDisplay : Boolean(line.isUnit),
        uom: String(line.uom || line.unitOfMass || uomFromDisplay || ''),
      };
    });
};
